/**
 * Child SceneObject blocks:
 * - trace/getChildCount — data block, returns number of children
 * - trace/setActiveChild — execution block, activates child by index, deactivates siblings
 */

import { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeNumber } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { resolveTarget } from './traceContext';
import { Logger } from '../../../utils/Logger';

// ============================================================================
// trace/getChildCount — data block
// ============================================================================

export class TraceGetChildCountBlock extends FlowGraphBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("value", RichTypeNumber);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        this.getDataOutput("value")!.setValue(target?.getChildren().length ?? 0, context);
    }

    public override getClassName(): string {
        return "TraceGetChildCountBlock";
    }
}

// ============================================================================
// trace/setActiveChild — execution block
// ============================================================================

export class TraceSetActiveChildBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("index", RichTypeNumber, 0);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.out._activateSignal(context);
            return;
        }

        const children = target.getChildren();
        if (children.length === 0) {
            Logger.warn(`[TraceSetActiveChildBlock] No children on ${target.name}`);
            this.out._activateSignal(context);
            return;
        }

        const rawIndex = this.getDataInput("index")!.getValue(context) as number;
        const index = Math.max(0, Math.min(Math.floor(rawIndex), children.length - 1));

        for (let i = 0; i < children.length; i++) {
            children[i].setActive(i === index);
        }

        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSetActiveChildBlock";
    }
}
